import {
  BodyLG,
  BodyLGB,
  BodyMDB,
  BodyXL,
  BodyXLB,
  Heading2XL,
  Heading3XL,
  Heading4XL,
  HeadingXL,
} from "../../../typography";

import { AccordionFeatureSectionProps } from "../types";
import { Link } from "react-router-dom";
import { RiveAnimation } from "../ui/RiveAnimation";
import { StandardButton } from "@verse_core/shared_components/buttons/StandardButton";
import { cn } from "@verse_core/shared_components/ClassName";
import { useState } from "react";

export const AccordionFeatureSection = ({
  title,
  sections,
  rivSrc,
  imageSrc,
  className,
  hasButton = true,
}: AccordionFeatureSectionProps) => {
  const [isOpen, setIsOpen] = useState(0);

  const handleIconHover = (
    e: React.MouseEvent<HTMLDivElement>,
    isExpanded: boolean,
  ) => {
    if (!isExpanded) {
      const imgElement = e.currentTarget.querySelector("img");
      if (imgElement) {
        imgElement.src =
          e.type === "mouseenter"
            ? "/landing_page_assets/icons/BluePlusIcon.svg"
            : "/landing_page_assets/icons/BlackPlusIcon.svg";
      }
    }
  };

  return (
    <div
      className={cn(
        "tw-flex tw-flex-col tw-items-start tw-gap-x-[3.5em] tw-gap-y-[1em] min440:tw-flex-row",
        className,
      )}
    >
      <div className="tw-flex tw-w-full tw-flex-col tw-gap-y-[48px] min440:tw-w-1/2">
        <div className="tw-flex tw-flex-col tw-gap-y-[64px]">
          <div className="tw-flex tw-flex-col tw-gap-y-[32px]">
            {title && (
              <>
                <Heading4XL className="tw-my-0 tw-hidden tw-text-blue-dark-3 min440:tw-flex">
                  {title}
                </Heading4XL>

                <Heading3XL className="tw-my-0 tw-text-blue-dark-3 min440:tw-hidden">
                  {title}
                </Heading3XL>
              </>
            )}
            {sections.map((section, index) => {
              const isExpanded = isOpen === index;
              return (
                <div
                  key={index}
                  className="tw-flex tw-flex-col tw-gap-y-[24px]"
                >
                  <div
                    className="tw-flex tw-cursor-pointer tw-flex-row tw-items-center tw-gap-x-[16px]"
                    onClick={() => {
                      setIsOpen(index === isOpen ? -1 : index);
                    }}
                    onMouseEnter={(e) => handleIconHover(e, isExpanded)}
                    onMouseLeave={(e) => handleIconHover(e, isExpanded)}
                  >
                    <img
                      src={
                        isExpanded
                          ? "/landing_page_assets/icons/minusIcon.svg"
                          : "/landing_page_assets/icons/BlackPlusIcon.svg"
                      }
                      alt={isExpanded ? "minusIcon" : "plusIcon"}
                      className="tw-h-[24px] tw-w-[24px] tw-cursor-pointer min440:tw-h-[32px] min440:tw-w-[32px]"
                    />
                    <Heading2XL className="tw-my-0 tw-hidden tw-text-blue-dark-3 min440:tw-flex">
                      {section.title}
                    </Heading2XL>
                    <HeadingXL className="tw-my-0 tw-text-blue-dark-3 min440:tw-hidden">
                      {section.title}
                    </HeadingXL>
                  </div>

                  <div
                    className={cn(
                      `tw-grid tw-transition-[grid-template-rows] tw-duration-300`,
                      isExpanded ? "tw-grid-rows-[1fr]" : "tw-grid-rows-[0fr]",
                    )}
                  >
                    <div className="tw-overflow-hidden">
                      <div className="tw-flex tw-flex-col tw-gap-y-[24px] tw-py-[1px]">
                        {section.description && (
                          <>
                            <BodyXL className="tw-my-0 tw-hidden tw-text-gray-2 min440:tw-flex">
                              {section.description}
                            </BodyXL>
                            <BodyLG className="tw-my-0 tw-text-gray-2 min440:tw-hidden">
                              {section.description}
                            </BodyLG>
                          </>
                        )}

                        {section.subtitle && (
                          <>
                            <HeadingXL className="tw-my-0 tw-hidden tw-text-blue-dark-3 min440:tw-flex">
                              {section.subtitle}
                            </HeadingXL>
                            <BodyXLB className="tw-my-0 tw-text-blue-dark-3 min440:tw-hidden">
                              {section.subtitle}
                            </BodyXLB>
                          </>
                        )}

                        {section.bulletPoints && (
                          <div>
                            <div className="tw-flex tw-flex-col tw-gap-y-[16px]">
                              {section.bulletPoints.map((point) => (
                                <div key={point.text}>
                                  <div className="tw-flex tw-items-start tw-gap-x-[8px]">
                                    <img
                                      src="/landing_page_assets/icons/BluePlus.svg"
                                      alt="plusIcon"
                                      className="tw-mt-[.3em] tw-h-[.8125em] tw-w-[.8125em] min440:tw-mt-[.45em] min440:tw-h-[1em] min440:tw-w-[1em]"
                                    />
                                    <BodyXL className="tw-my-0 tw-hidden tw-text-gray-2 min440:tw-block">
                                      <span className="tw-text-blue-1">
                                        {point.textAccent}
                                      </span>{" "}
                                      {point.text}
                                    </BodyXL>
                                    <BodyLG className="tw-my-0 tw-text-gray-2 min440:tw-hidden">
                                      {point.text}
                                    </BodyLG>
                                  </div>
                                  {point.subBulletPoints && (
                                    <ul className="tw-ml-[0px]">
                                      {point.subBulletPoints.map((subPoint) => (
                                        <li key={subPoint.text}>
                                          <BodyXL className="tw-my-0 tw-ml-[.5em] tw-hidden tw-w-[97%] tw-whitespace-normal tw-text-gray-2 min440:tw-block">
                                            <span className="tw-font-medium">
                                              {subPoint.textAccent}
                                            </span>{" "}
                                            {subPoint.text}
                                          </BodyXL>
                                          <BodyLG className="tw-my-0 tw-text-gray-2 min440:tw-hidden">
                                            <span className="tw-font-medium">
                                              {subPoint.textAccent}
                                            </span>{" "}
                                            {subPoint.text}
                                          </BodyLG>
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {hasButton && (
                          <StandardButton
                            onClick={() => {}}
                            style={"primary-blue"}
                            className="tw-mb-[2.625em] tw-mt-0 tw-w-[13.75em] tw-rounded-full tw-px-[2em] tw-py-[1em] tw-text-[16px]"
                          >
                            <Link to="/contact" className="tw-no-underline">
                              <BodyLGB className="tw-my-0 tw-hidden tw-text-white min440:tw-block">
                                Get a demo
                              </BodyLGB>
                              <BodyMDB className="tw-my-0 tw-text-white min440:tw-hidden">
                                Get a demo
                              </BodyMDB>
                            </Link>
                          </StandardButton>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div className="tw-flex tw-w-full min440:tw-relative min440:tw-min-h-[600px] min440:tw-w-1/2">
        <div className="tw-w-full min440:tw-absolute min440:tw-inset-0">
          <RiveAnimation
            src={rivSrc}
            className="tw-aspect-square tw-w-full"
            fallbackSrc={imageSrc}
            fallbackClassName="tw-w-full tw-rounded-[2em]"
          />
        </div>
      </div>
    </div>
  );
};
